import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap5';
import { useSelector } from 'react-redux';
import { hooks as commonHooks } from '@sitebuilder/common';
import { StateShape as ReduxStateShape } from '../../reducers';

interface PropsShape {
    /** passes selected users to parent component */
    onSelectUser: (users: Record<string, unknown> | any[]) => void;
    /** toggle the dropdown display */
    display: boolean;
    /** make display prop false to close the dropdown */
    closeDropdown: () => void;
    /** required to check when handling closing the dropdown on click outside of component */
    toggleIconRef: React.MutableRefObject<any>;
}

const UserFilterInput = ({ onSelectUser, display, closeDropdown, toggleIconRef }: PropsShape) => {
    const i18n = commonHooks.useLocalization();
    const [_users, setUsers] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<any>({});
    const [value, setValue] = useState('');
    const dropdownToggleRef = useRef<any>(null);
    const dropdownRef = useRef<any>(null);

    const { users } = useSelector((state: ReduxStateShape) => ({
        users: state.users?.users as any,
    }));

    useEffect(() => {
        if (display && dropdownToggleRef?.current) dropdownToggleRef.current.click();
    }, [display]);

    useEffect(() => {
        setUsers(users);
    }, [users]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setValue(e.target.value);
        setUsers(users.filter((user: any) => user.Email.includes(value)));
    };

    const handleUserSelect = (e: any, user: any) => {
        setSelectedUsers({ ...selectedUsers, [user.Id]: e.target.checked });
    };

    useEffect(() => {
        if (Object.keys(selectedUsers).length > 0) {
            const usersSelected = Object.keys(selectedUsers).filter((key) => selectedUsers[key]);
            onSelectUser(usersSelected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUsers]);

    // handle if click outside of drodown and toggle icon
    const handleClickOutside = (e: any) => {
        // check if click is outside od dropdown and toggle icon
        if (
            dropdownRef?.current &&
            !dropdownRef.current.contains(e.target) &&
            toggleIconRef?.current &&
            !toggleIconRef.current.contains(e.target)
        ) {
            // close drodown on click outside
            closeDropdown();
        }
    };

    useEffect(() => {
        // event listener to check if click outside of component
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // remove the event listener
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownRef]);

    return (
        <>
            {display && (
                <Dropdown className="user-dropdown" ref={dropdownRef} show={display}>
                    <Dropdown.Toggle ref={dropdownToggleRef}>
                        <label id="search-input-label" className="search-input-wrapper">
                            {!value && <i className="serach-icon fas fa-search" />}
                            <input
                                className="text-input filter-search-input"
                                type="text"
                                value={value}
                                aria-label={i18n.t('search')}
                                placeholder={i18n.t('search')}
                                onChange={handleSearchChange}
                                autoFocus
                            />
                        </label>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {_users?.length > 0 &&
                            _users.map((user: any, index: number) => (
                                <div className="dropdown-item" key={`user-${index}`}>
                                    <label className="user-select">
                                        <input
                                            className="user-select-input"
                                            type="checkbox"
                                            defaultChecked={selectedUsers[user.Id]}
                                            aria-label={i18n.t('select_user')}
                                            onChange={(e) => handleUserSelect(e, user)}
                                        />
                                        {user.Email}
                                    </label>
                                </div>
                            ))}
                    </Dropdown.Menu>
                </Dropdown>
            )}
        </>
    );
};

export default UserFilterInput;
